import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Table, Button } from "reactstrap";
import {
  Loading, WarningAlert, PaginationControls, ItemsPerPageSelector, SortableHeaderCell, EntityRow, ConfirmationModal,
  NotificationToast, SimpleFormModal, SearchBar, InformationalModal
} from "../components";
import useSortableData from "../hooks/useSortableData";
import { useEntity } from "../hooks/useEntity";
import { steeringPreferencesApi } from "../api/entitiesApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { steeringPreferenceFields, steeringPreferenceDefaultValues } from "../utils/entityFormFields";
import { transformPreferences } from "../utils/transformPreferences"
import { Link } from "react-router-dom";

// styles
import "../App.css";

export function SteeringPreferencesComponent() {
  const { data: steeringPreferences, isLoading, error, setData: setSteeringPreferences, setError } = useEntity(steeringPreferencesApi);
  const { items: sortedSteeringPreferences, requestSort, sortConfig } = useSortableData(steeringPreferences, { key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [steeringPreferencesPerPage, setSteeringPreferencesPerPage] = useState(50);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [deleteData, setDeleteData] = useState({ open: false, steeringPreferenceId: null });
  const [notification, setNotification] = useState({ visible: false, title: "", message: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState({ open: false, title: "", message: "" });


  const totalPages = Math.ceil(steeringPreferences.length / steeringPreferencesPerPage);
  const indexOfLastSteeringPreference = currentPage * steeringPreferencesPerPage;
  const indexOfFirstSteeringPreference = indexOfLastSteeringPreference - steeringPreferencesPerPage;
  const itemsPerPage = [50, 100];
  const filteredSteeringPreferences = sortedSteeringPreferences.filter(steeringPreference => {
    if (!searchTerm) return true;
    const lowerTerm = searchTerm.toLowerCase();
    return (
      (steeringPreference.id && steeringPreference.id.toString().toLowerCase().includes(lowerTerm)) ||
      (steeringPreference.customer_id && steeringPreference.customer_id.toString().toLowerCase().includes(lowerTerm)) ||
      (steeringPreference.site_id && steeringPreference.site_id.toString().toLowerCase().includes(lowerTerm)) ||
      (steeringPreference.preference_type && steeringPreference.preference_type.toString().toLowerCase().includes(lowerTerm)) ||
      (steeringPreference.preferences && JSON.stringify(steeringPreference.preferences).toLowerCase().includes(lowerTerm))
    );
  });
  const currentSteeringPreferences = filteredSteeringPreferences.slice(indexOfFirstSteeringPreference, indexOfLastSteeringPreference);
  function toggleDropdown() { setDropdownOpen(prevState => !prevState); }
  function handlePageChange(pageNumber) { setCurrentPage(pageNumber); }
  function handleDropdownChange(number) {
    setSteeringPreferencesPerPage(number);
    setCurrentPage(1);
  };

  function handleEdit(steeringPreference) {
    setCurrentForm({ mode: "edit", data: steeringPreference });
  };

  function handleAdd() {
    setCurrentForm({ mode: "add", data: { customer_id: "", site_id: "" } });
  }

  async function handleSaveForm(formData) {
    try {
      if (currentForm.mode === "edit") {
        await steeringPreferencesApi.updateEntity(formData.id, formData);
        setSteeringPreferences(prevData =>
          prevData.map(steeringPreference =>
            steeringPreference.id === formData.id ? formData : steeringPreference
          )
        );
        setNotification({ visible: true, title: "SteeringPreference Edited", message: "1 SteeringPreference has been edited" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      } else {
        const newSteeringPreference = await steeringPreferencesApi.addEntity(formData);
        setSteeringPreferences(prevData => [...prevData, newSteeringPreference]);
        setNotification({ visible: true, title: "SteeringPreference Added", message: "1 SteeringPreference has been added" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      }
    } catch (err) {
      setErrorMessage({ open: true, title: currentForm.mode === "edit" ? "Error Updating SteeringPreference" : "Error Adding SteeringPreference", message: err.message });
    } finally {
      setCurrentForm(null);
    }
  }

  function handleCancelFormModal() {
    setCurrentForm(null);
  }

  function openDeleteModal(steeringPreference) {
    setDeleteData({ open: true, steeringPreferenceId: steeringPreference.id });
  }

  async function confirmDelete() {
    try {
      await steeringPreferencesApi.deleteEntity(deleteData.steeringPreferenceId);
      setSteeringPreferences((prevSteeringPreferences) =>
        prevSteeringPreferences.filter((steeringPreference) => steeringPreference.id !== deleteData.steeringPreferenceId)
      );
      setNotification({ visible: true, title: "SteeringPreference Deleted", message: "1 SteeringPreference has been deleted" });
      setTimeout(() => {
        setNotification({ visible: false, title: "", message: "" });
      }, 2000);
    } catch (err) {
      setErrorMessage({ open: true, title: "Error Deleting SteeringPreference", message: err.message })
    } finally {
      setDeleteData({ open: false, steeringPreferenceId: null });
    }
  }

  function cancelDelete() {
    setDeleteData({ open: false, steeringPreferenceId: null });
  };

  function dismissInformationalModal() {
    setErrorMessage({ open: false, title: "", message: "" })
  }

  if (isLoading) {
    return <Loading />;
  }

  const displayError = error
    ? error.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ))
    : "Ooops! No steeringPreferences found";

  return (
    <>
      {steeringPreferences.length === 0 && (
        <div className="d-flex flex-column align-items-center">
          <WarningAlert message={displayError} />
          <br />
          <div className="d-flex justify-content-center">
            <Button
              color="primary"
              onClick={(e) => {
                e.currentTarget.blur();
                setError(null);
                handleAdd();
              }}
              aria-label={error ? "Create First SteeringPreference" : "Create SteeringPreference"}
            >
              Create
            </Button>
          </div>
        </div>
      )}

      {steeringPreferences.length > 0 && (
        <div className="mb-5">
          <div className="d-flex justify-content-end align-items-center mb-2">
            <div style={{ marginRight: "1rem" }}>
              <Button
                color="transparent"
                onClick={(e) => {
                  e.currentTarget.blur();
                  handleAdd();
                }}
                aria-label="Add SteeringPreference"
              >
                <FontAwesomeIcon icon="plus" />
              </Button>
            </div>
            <div>
              <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            </div>
          </div>

          {filteredSteeringPreferences.length > 0 ? (
            <>
              <Table bordered hover responsive size="sm" striped>
                <thead>
                  <tr>
                    <SortableHeaderCell columnKey={"customer_id"} label={"Customer_id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"id"} label={"Id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"site_id"} label={"Site_id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"preference_type"} label={"Preference_type"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"preferences"} label={"Preferences"} currentSort={sortConfig} onSort={requestSort} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentSteeringPreferences.map((steeringPreference) => (
                    <EntityRow
                      key={steeringPreference.id}
                      entity={steeringPreference}
                      fields={["customer_id", "id", "site_id", "preference_type", "preferences"]}
                      onEdit={handleEdit}
                      onDelete={openDeleteModal}
                      isSelected={
                        (currentForm && currentForm.mode === "edit" && steeringPreference.id === currentForm.data.id) ||
                        steeringPreference.id === deleteData.steeringPreferenceId
                      }
                      renderField={(field, value) => {
                        if (field === "customer_id") {
                          return <Link to={`/customerDetails/${value}`}>{value.substring(0, 4)}</Link>;
                        }
                        if (field === "preferences") {
                          return <pre>{JSON.stringify(transformPreferences(value), null, 2)}</pre>;
                        }
                        return value;
                      }}
                    />
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center">
                <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                <ItemsPerPageSelector
                  items={itemsPerPage}
                  currentSelection={steeringPreferencesPerPage}
                  onSelect={handleDropdownChange}
                  isOpen={dropdownOpen}
                  toggle={toggleDropdown}
                />
              </div>
            </>
          ) : (
            <WarningAlert message="Ooops! No steeringPreferences found" />
          )}
        </div>
      )}

      <InformationalModal
        isOpen={errorMessage.open} title={errorMessage.title} message={errorMessage.message}
        onCancel={dismissInformationalModal}
      />

      <ConfirmationModal
        isOpen={deleteData.open} title="Delete SteeringPreference?" message="Are you sure you want to delete this steeringPreference?"
        onConfirm={confirmDelete} onCancel={cancelDelete} confirmLabel="OK" cancelLabel="Cancel"
      />

      <SimpleFormModal
        isOpen={!!currentForm} title={currentForm ? (currentForm.mode === "edit" ? "Edit SteeringPreference" : "Add SteeringPreference") : ""}
        initialData={currentForm ? currentForm.data : null} fields={steeringPreferenceFields()} onSave={handleSaveForm} onCancel={handleCancelFormModal}
        defaultValues={steeringPreferenceDefaultValues}
      />

      {notification.visible && (
        <NotificationToast icon="exclamation-triangle" title={notification.title} message={notification.message} fade={true} />
      )}
    </>
  );
};

export default withAuthenticationRequired(SteeringPreferencesComponent, {
  onRedirecting: () => <Loading />,
});
